<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

import i18next from 'i18next';
import { getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';

const { proxy } = getCurrentInstance();
const router = useRouter();

const password = ref('');
const password_confirmation = ref('');
const passwordVisible = ref(false);
const loading = ref(false);
const hasMinLength = computed(() => password.value.length >= 8);
const hasLetter = computed(() => /[a-zA-Z]/.test(password.value));
const hasMixedCase = computed(() => /[a-z]/.test(password.value) && /[A-Z]/.test(password.value));
const hasNumber = computed(() => /[0-9]/.test(password.value));
const passwordsMatch = computed(() => password.value === password_confirmation.value);
const passwordRules = computed(() => [
    { label: 'change_password_error_letter', valid: hasLetter.value },
    { label: 'change_password_error_length', valid: hasMinLength.value },
    { label: 'change_password_error_case', valid: hasMixedCase.value },
    { label: 'change_password_error_number', valid: hasNumber.value },
    { label: 'change_password_error_match', valid: passwordsMatch.value }
]);
const allPasswordRulesValid = computed(() => {
    return passwordRules.value.every((rule) => rule.valid);
});

const togglePassword = () => {
    passwordVisible.value = !passwordVisible.value;
};
const store = useStore();

const user = computed(() => store.getters['user/get']);

const showIconValue = computed(() => {
    if (passwordVisible.value) {
        return 'fi fi-rs-crossed-eye';
    } else {
        return 'fi fi-rs-eye';
    }
});

const submitPassword = async () => {
    if (!allPasswordRulesValid.value) {
        return;
    }
    loading.value = true;
    const sendUser = {
        email_confirmation: user.value.email,
        id: user.value.id,
        email: user.value.email,
        first_name: user.value.first_name,
        last_name: user.value.last_name,
        password: password.value,
        password_confirmation: password_confirmation.value
    };

    await store
        .dispatch('user/update', sendUser)
        .then(() => {
            proxy.$toast(i18next.t('change_password_success'), {
                type: 'success',
            });
            router.push('/profile');
        })
        .catch((error) => {
            this.$toast(error.response.data.message);
            return Promise.reject(error);
        })
        .finally(() => {
            loading.value = false;
            password.value = '';
            password_confirmation.value = '';
        });
};
</script>
<template>
    <div class="max-w-lg mx-auto">
        <Loading :loading="loading" :inset="true" class="my-4">
            <div class="py-4 border-b border-gray-300">
                <h2 class="text-xl font-semibold">
                    {{ $t('change_password') }}
                </h2>
                <p class="mt-2 text-gray-600">
                    {{ $t('change_password_description') }}
                </p>
            </div>

            <div class="my-8 space-y-6">
                <div class="relative justify-center">
                    <Input
                        :type="passwordVisible ? 'text' : 'password'"
                        name="password"
                        :label="$t('account_password')"
                        :strengthMeter="true"
                        v-model:value="password"
                        class="pr-12"
                    />

                    <button type="button" aria-label="Toggle password visibility" class="absolute right-3 top-2 text-gray-500 hover:text-gray-700" @click="togglePassword">
                        <i :class="showIconValue" />
                    </button>
                </div>

                <div class="relative justify-center">
                    <Input
                        :type="passwordVisible ? 'text' : 'password'"
                        name="password"
                        :label="$t('account_password_repeat')"
                        v-model:value="password_confirmation"
                        class="pr-12"
                    />

                    <button type="button" aria-label="Toggle password visibility" class="absolute right-3 top-2 text-gray-500 hover:text-gray-700" @click="togglePassword">
                        <i :class="showIconValue" />
                    </button>
                </div>

                <ul class="space-y-1 mt-4 text-sm">
                    <li v-for="rule in passwordRules" :key="rule.label" class="flex items-center gap-2 leading-5">
                        <i class="fi text-lg" :class="rule.valid ? 'text-green fi fi-rs-check-circle' : 'text-red fi fi-rr-cross-circle'" />
                        <span class="transition-colors duration-300" :class="rule.valid ? 'text-gray-700' : 'text-red-500'">
                            {{ $t(rule.label) }}
                        </span>
                    </li>
                </ul>

                <FormButton
                    ref="confettiBtn"
                    type="button"
                    @click="submitPassword"
                    :disabled="!allPasswordRulesValid"
                    :formComponentId="'customer_'"
                    class="w-auto"
                    styling="primary flex items-center gap-3"
                >
                    {{ $t('plain_update') }}
                    <transition name="fade">
                        <FontAwesomeIcon v-show="loading" :icon="['fas', 'circle-notch']" class="animate-spin" />
                    </transition>
                </FormButton>
            </div>
        </Loading>
    </div>
</template>
